// Central de avisos: os avisos operacionais abertos da conta (o servidor já
// filtra pelo papel do usuário). Lido pela sidebar (badge) e pela página.
// Evento do cable é GATILHO, não dado — vira refetch (a policy por papel mora
// no endpoint); o refetch periódico de 45 s cobre cable caído.
import OpsNoticesAPI from 'dashboard/api/opsNotices';

export const types = {
  START_FETCH: 'START_OPS_NOTICES_FETCH',
  SET_NOTICES: 'SET_OPS_NOTICES',
  REMOVE_NOTICE: 'REMOVE_OPS_NOTICE',
  SET_UI_FLAG: 'SET_OPS_NOTICES_UI_FLAG',
};

export const SEVERITIES = ['critical', 'warning', 'info'];

// Corrida resolver × fetch em voo: um GET que saiu ANTES de o resolve
// terminar pode voltar com o aviso ainda aberto e ressuscitá-lo na tela. Cada
// fetch leva um número de sequência; o aviso resolvido aqui guarda o número
// do último fetch iniciado até ali e é filtrado de toda resposta com número
// ≤ esse. O primeiro fetch iniciado DEPOIS do resolve é a verdade do
// servidor: limpa a marca (se o aviso voltar ali, voltou de verdade).
export const state = {
  records: [],
  // Total ABERTO por severidade, do servidor (`meta`). A lista vem cortada em
  // 200; contar `records` mentiria a partir daí. Sem resposta ainda = null.
  totals: null,
  fetchSeq: 0,
  locallyResolved: [], // [{ id, seq }]
  uiFlags: {
    isFetching: false,
    hasLoaded: false,
    // Último fetch falhou: a tela diz que a lista pode estar velha em vez de
    // mostrar a lista antiga como se fosse atual.
    hasError: false,
    // Pedido de refetch que chegou com outro em voo: roda uma vez ao final
    // (rajada de eventos do cable não vira rajada de requests).
    refetchQueued: false,
  },
};

export const getters = {
  getNotices: $state => $state.records,
  getUIFlags: $state => $state.uiFlags,
  getGroupedBySeverity: $state =>
    SEVERITIES.map(severity => ({
      severity,
      total: $state.totals?.[severity] ?? 0,
      notices: $state.records.filter(notice => notice.severity === severity),
    })).filter(group => group.notices.length > 0),
  // Lista cortada pelo servidor: quantos vieram × quantos estão abertos.
  getTruncation: $state => {
    const total = SEVERITIES.reduce(
      (sum, severity) => sum + ($state.totals?.[severity] ?? 0),
      0
    );
    return total > $state.records.length
      ? { shown: $state.records.length, total }
      : null;
  },
  // Badge da sidebar: só o que pede ação (crítico/atenção), pelo TOTAL aberto.
  // Tom crítico se houver ao menos um crítico.
  getBadge: $state => {
    const critical = $state.totals?.critical ?? 0;
    const warning = $state.totals?.warning ?? 0;
    return {
      count: critical + warning,
      tone: critical > 0 ? 'critical' : 'warning',
    };
  },
};

export const actions = {
  fetch: async ({ commit, dispatch, state: $state }) => {
    if ($state.uiFlags.isFetching) {
      commit(types.SET_UI_FLAG, { refetchQueued: true });
      return;
    }
    commit(types.SET_UI_FLAG, { isFetching: true, refetchQueued: false });
    commit(types.START_FETCH);
    const seq = $state.fetchSeq;
    try {
      const { data } = await OpsNoticesAPI.get();
      commit(types.SET_NOTICES, {
        records: data.payload,
        totals: data.meta.by_severity,
        seq,
      });
      commit(types.SET_UI_FLAG, { hasError: false, hasLoaded: true });
    } catch (error) {
      commit(types.SET_UI_FLAG, { hasError: true });
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
    if ($state.uiFlags.refetchQueued) await dispatch('fetch');
  },

  resolve: async ({ commit }, { id, resolution }) => {
    await OpsNoticesAPI.resolve(id, resolution);
    commit(types.REMOVE_NOTICE, id);
  },
};

export const mutations = {
  [types.START_FETCH]($state) {
    $state.fetchSeq += 1;
  },
  [types.SET_NOTICES]($state, { records, totals, seq }) {
    const stillLocal = $state.locallyResolved.filter(entry => entry.seq >= seq);
    const hidden = new Set(stillLocal.map(entry => entry.id));
    $state.locallyResolved = stillLocal;
    $state.records = records.filter(notice => !hidden.has(notice.id));
    // O total do servidor ainda conta o aviso resolvido aqui que a resposta
    // trouxe aberto (corrida acima): desconta os que foram escondidos.
    const adjusted = { ...totals };
    records
      .filter(notice => hidden.has(notice.id))
      .forEach(notice => {
        adjusted[notice.severity] -= 1;
      });
    $state.totals = adjusted;
  },
  [types.REMOVE_NOTICE]($state, id) {
    const removed = $state.records.find(notice => notice.id === id);
    if (removed && $state.totals) {
      $state.totals = {
        ...$state.totals,
        [removed.severity]: $state.totals[removed.severity] - 1,
      };
    }
    $state.records = $state.records.filter(notice => notice.id !== id);
    $state.locallyResolved = [
      ...$state.locallyResolved.filter(entry => entry.id !== id),
      { id, seq: $state.fetchSeq },
    ];
  },
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
